/**
 * WebAuthn platform authenticator helpers for desktop fingerprint unlock.
 * Falls back gracefully when unavailable (USB IPC path can replace later).
 */

function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function base64ToBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function canUsePlatformBiometrics(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.protocol === 'file:') return false;
  if (window.isSecureContext === false) return false;
  return !!window.PublicKeyCredential;
}

/** @returns credential id (base64) */
export async function enrollPlatformFingerprint(userId: string, displayName: string): Promise<string> {
  if (!canUsePlatformBiometrics()) {
    throw new Error('Biometrics not available on this device');
  }
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const userIdBytes = new TextEncoder().encode(userId.slice(0, 64));
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge,
      rp: { name: 'Sale Desk POS', id: window.location.hostname || 'localhost' },
      user: {
        id: userIdBytes,
        name: userId,
        displayName,
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'preferred',
      },
      timeout: 60_000,
    },
  })) as PublicKeyCredential | null;

  if (!cred) throw new Error('Fingerprint enrollment cancelled');
  return bufferToBase64(cred.rawId);
}

/** Verify fingerprint; returns credential id if assertion succeeds for stored id */
export async function verifyPlatformFingerprint(credentialIdB64: string): Promise<string> {
  if (!canUsePlatformBiometrics()) {
    throw new Error('Biometrics not available on this device');
  }
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge,
      allowCredentials: [
        {
          type: 'public-key',
          id: base64ToBuffer(credentialIdB64),
          transports: ['internal'],
        },
      ],
      userVerification: 'required',
      timeout: 60_000,
    },
  })) as PublicKeyCredential | null;

  if (!assertion) throw new Error('Fingerprint verification cancelled');
  return bufferToBase64(assertion.rawId);
}
